import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics } from '@/mocks/comics';

export default function Chapter() {
  const { comicSlug, chapterId } = useParams<{ comicSlug: string; chapterId: string }>();
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const comic = comics.find((c) => c.slug === comicSlug);

  if (!comic || !chapterId) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-800 mb-4">章节未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const chapterIndex = comic.chapters.findIndex((ch) => ch.id === chapterId);
  const chapter = comic.chapters[chapterIndex];
  const prevChapter = chapterIndex > 0 ? comic.chapters[chapterIndex - 1] : null;
  const nextChapter = chapterIndex < comic.chapters.length - 1 ? comic.chapters[chapterIndex + 1] : null;

  if (!chapter) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-800 mb-4">章节未找到</h1>
            <Link to={`/comic/${comicSlug}`} className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回漫画详情</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${comic.title} ${chapter.title} - 韩漫网站免费阅读`}
        description={`韩漫网站提供${comic.title} ${chapter.title}免费在线阅读。高品质韩漫，舒适阅读体验。`}
        keywords={`${comic.title},${chapter.title},韩漫,韩国漫画,免费阅读`}
        canonicalUrl={`https://www.manhwa-site.com/chapter/${comicSlug}/${chapterId}`}
        ogType="article"
      />
      <ScrollToTop />
      <Navbar />

      <main className={`min-h-screen pt-16 ${theme === 'dark' ? 'bg-foreground-950 text-background-200' : 'bg-background-50'}`}>
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title, href: `/comic/${comicSlug}` },
              { label: chapter.title },
            ]}
          />

          <div className={`flex items-center justify-between py-4 mb-4 border-b ${theme === 'dark' ? 'border-foreground-800' : 'border-background-200'}`}>
            <div>
              <h1 className="text-lg font-bold font-heading">{comic.title}</h1>
              <p className="text-sm mt-1 opacity-70">{chapter.title}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer ${
                  theme === 'dark' ? 'bg-foreground-800 text-background-200 hover:bg-foreground-700' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
                aria-label="切换阅读模式"
              >
                <i className={theme === 'dark' ? 'ri-sun-line' : 'ri-moon-line'}></i>
              </button>
              <Link
                to={`/comic/${comicSlug}`}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200 ${
                  theme === 'dark' ? 'bg-foreground-800 text-background-200 hover:bg-foreground-700' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
                aria-label="返回详情"
              >
                <i className="ri-list-check"></i>
              </Link>
            </div>
          </div>

          <article className={`py-8 ${theme === 'dark' ? 'text-background-200' : 'text-foreground-800'}`}>
            <div className={`rounded-lg p-8 md:p-12 mb-8 ${theme === 'dark' ? 'bg-foreground-900' : 'bg-background-100/60'}`}>
              <div className="max-w-2xl mx-auto space-y-4">
                <p className="text-base leading-loose indent-8">
                  首尔的清晨，阳光透过窗帘的缝隙洒进了房间。智恩睁开惺忪的睡眼，伸了个懒腰。今天是她在新公司上班的第一天，心里既期待又紧张。
                </p>
                <p className="text-base leading-loose indent-8">
                  她精心挑选了一套简约的职场装扮——白色的衬衫搭配深灰色的铅笔裙，头发简单地扎成了低马尾。对着镜子深吸一口气，智恩给自己打气：&ldquo;加油，你可以的！&rdquo;
                </p>
                <p className="text-base leading-loose indent-8">
                  地铁上人潮涌动，智恩好不容易挤上了车。她拿出手机，再次确认了公司的地址和入职流程。窗外的城市风景飞速掠过，高楼大厦在晨光中显得格外耀眼。
                </p>
                <p className="text-base leading-loose indent-8">
                  到达公司大楼前，智恩抬头仰望这座现代化的建筑，心中涌起一股自豪感。这是她梦寐以求的广告公司，能够在这里工作是她大学四年努力的目标。
                </p>
                <p className="text-base leading-loose indent-8">
                  走进大楼，前台的工作人员热情地接待了她。&ldquo;林智恩小姐是吗？请稍等，我会通知您的主管。&rdquo;智恩微笑着点了点头，坐在了等候区的沙发上。
                </p>
                <p className="text-base leading-loose indent-8">
                  不一会儿，一位穿着深蓝色西装的男士走了过来。他身材挺拔，面容冷峻，眼神中带着一丝疏离。&ldquo;林智恩？我是艺术总监金宇镇，跟我来。&rdquo;他的声音低沉而简短。
                </p>
                <p className="text-base leading-loose indent-8">
                  智恩连忙站起身，跟在他的身后。她能感觉到周围同事投来的好奇目光，让她有些不自在。金总监的步伐很快，智恩几乎要小跑才能跟上。
                </p>
                <p className="text-base leading-loose indent-8">
                  &ldquo;这是你的工位。&rdquo;金总监在一张靠窗的办公桌前停下，&ldquo;今天先熟悉一下公司的项目流程，有什么不懂的可以问旁边的同事。&rdquo;说完他便转身离开，连一句&ldquo;欢迎&rdquo;都没有。
                </p>
                <p className="text-base leading-loose indent-8">
                  智恩看着他的背影，心里有些失落。旁边的同事——一个扎着丸子头的可爱女生凑了过来，小声说：&ldquo;别在意，金总监对谁都这样，外冷内热。我叫李敏静，以后有什么问题找我就行！&rdquo;
                </p>
                <p className="text-base leading-loose indent-8">
                  就这样，智恩的职场生活开始了。她不知道的是，在这个办公室里，等待她的不仅有挑战和成长，还有一段意想不到的感情。
                </p>
              </div>
            </div>
          </article>

          <div className={`flex items-center justify-between py-6 border-t ${theme === 'dark' ? 'border-foreground-800' : 'border-background-200'}`}>
            {prevChapter ? (
              <Link
                to={`/chapter/${comicSlug}/${prevChapter.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  theme === 'dark'
                    ? 'bg-foreground-800 text-background-200 hover:bg-foreground-700'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className="ri-arrow-left-line"></i> {prevChapter.title}
              </Link>
            ) : (
              <div></div>
            )}

            {nextChapter ? (
              <Link
                to={`/chapter/${comicSlug}/${nextChapter.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  theme === 'dark'
                    ? 'bg-primary-600 text-background-50 hover:bg-primary-500'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                {nextChapter.title} <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <div></div>
            )}
          </div>

          <div className={`text-center py-6 ${theme === 'dark' ? 'text-foreground-500' : 'text-foreground-400'}`}>
            <p className="text-xs">
              — 本章完 · 共 {comic.chapters.length} 话 · 更新于 {chapter.updatedAt} —
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}